import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BedDouble, CarTaxiFront, Download, Palmtree, Plane } from "lucide-react";
import { Empty, PrimaryBtn } from "@/components/Common";
import { downloadPdf, errMsg, http, inr } from "@/lib/api";
import { payWithRazorpay } from "@/lib/pay";

const ICON = { flight: Plane, hotel: BedDouble, cab: CarTaxiFront, package: Palmtree };
export const STATUS = {
  pending_payment: ["Awaiting payment", "bg-amber-100 text-amber-800"],
  confirmed: ["Confirmed", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-slate-200 text-slate-600"],
  completed: ["Completed", "bg-sky-100 text-sky-800"],
};

export const StatusBadge = ({ s, testid }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS[s][1]}`} data-testid={testid}>{STATUS[s][0]}</span>
);

export default function MyBookings() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["my-bookings"], queryFn: () => http.get("/bookings/me").then((r) => r.data) });
  const reload = () => qc.invalidateQueries({ queryKey: ["my-bookings"] });

  const pay = async (b) => {
    try {
      const { data: d } = await http.post(`/bookings/${b.id}/pay`);
      if ((await payWithRazorpay(d.payment, b.id)) === "confirmed") toast.success("Payment successful");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async (b) => {
    if (!window.confirm(`Cancel booking ${b.ref}?`)) return;
    try {
      await http.post(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-8" data-testid="my-bookings-page">
      <p className="eyebrow">Your trips</p>
      <h1 className="font-display mt-2 text-4xl font-bold text-[#0D1B2A] sm:text-5xl">My bookings</h1>
      <div className="mt-10 space-y-4">
        {isLoading && <div className="h-24 animate-pulse rounded-2xl bg-white" />}
        {!isLoading && !data.length && <Empty testid="bookings-empty">No bookings yet. Your flights, hotels, cabs and holidays will appear here.</Empty>}
        {data.map((b) => {
          const Icon = ICON[b.type];
          return (
            <div key={b.id} className="grid gap-4 rounded-2xl border border-[#E5E0D8] bg-white p-5 sm:grid-cols-[auto_1fr_auto] sm:items-center" data-testid={`booking-row-${b.ref}`}>
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#F4F0EA]"><Icon className="h-5 w-5 text-[#E07A5F]" /></span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-slate-500">{b.ref}</span>
                  <StatusBadge s={b.status} testid={`booking-status-${b.ref}`} />
                </div>
                <p className="mt-1 font-semibold">{b.title}</p>
                <p className="text-sm text-slate-500">{b.travel_date}{b.end_date ? ` → ${b.end_date}` : ""} · {b.contact_name}</p>
              </div>
              <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                <p className="text-lg font-bold">{inr(b.amount)}</p>
                {b.discount > 0 && <p className="text-xs text-emerald-700" data-testid={`booking-saved-${b.ref}`}>Saved {inr(b.discount)} with {b.coupon_code}</p>}
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  {b.status !== "cancelled" && (
                    <button onClick={() => downloadPdf(b).catch((e) => toast.error(errMsg(e)))} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#0D1B2A] px-4 text-sm font-semibold hover:bg-[#0D1B2A] hover:text-white transition-colors" data-testid={`booking-pdf-${b.ref}`}>
                      <Download className="h-4 w-4" />{b.status === "confirmed" || b.status === "completed" ? "E-ticket" : "Invoice"}
                    </button>
                  )}
                  {b.status === "pending_payment" && b.razorpay_order_id && <PrimaryBtn className="h-9 px-4" onClick={() => pay(b)} data-testid={`booking-pay-${b.ref}`}>Pay now</PrimaryBtn>}
                  {["pending_payment", "confirmed"].includes(b.status) && (
                    <button onClick={() => cancel(b)} className="h-9 rounded-full border border-[#E5E0D8] px-4 text-sm hover:border-red-300 hover:text-red-600" data-testid={`booking-cancel-${b.ref}`}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
